// Operator › Cost to serve (`/operator/cost?month=&view=`; M5-05, 11 §Two money views; Reports /
// Ops UX pass 2026-10-21): what each DE costs the agency (model, supervision, other) against what it
// charges the client, per DE, per client company and per template (`GET /v1/operator/cost-to-serve`,
// one call). The 56 px location header, a 40 px toolbar (month, the three views as tabs), four
// compact tiles (one currency at a time: money is never summed across currencies), and the
// table: margin as a bar of what was charged, a loss in red, Open DE and Open company on hover.
import {
  EmptyState,
  InlineBar,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  StatTile,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TabsContent,
  Toolbar,
  ViewTabs,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ArrowUpRightIcon,
  BotIcon,
  Building2Icon,
  ReceiptIcon,
  TimerIcon,
  TrendingUpIcon,
} from "lucide-react";
import { type ReactNode, useMemo } from "react";
import type { Api } from "../../api/client";
import { formatMoney } from "../format";
import {
  OpsBody,
  OpsFrame,
  operatorCrumb,
  RowActions,
  RowLinkAction,
  RowSub,
  TABLE_ROW_HOVER,
  Tiles,
} from "../ops-ux";
import { LoadError, Loading } from "../screen-parts";
import { CoverageNotice, Money } from "./agency-parts";
import {
  type CostToServe,
  costToServeQuery,
  hMm,
  monthLabel,
  recentMonths,
} from "./agency-queries";

export const COST_VIEWS = ["des", "clients", "templates"] as const;
export type CostView = (typeof COST_VIEWS)[number];
const VIEW_LABELS: Record<CostView, string> = {
  des: "Per DE",
  clients: "Per client",
  templates: "Per template",
};

type MoneyRow = Pick<
  CostToServe["clients"][number],
  | "currency"
  | "charged_minor"
  | "model_minor"
  | "supervision_minor"
  | "other_minor"
  | "cost_minor"
  | "margin_minor"
>;

export function CostToServePage({
  api,
  month,
  view,
  onChange,
}: {
  api: Api;
  month: string;
  view: CostView;
  onChange: (next: { month: string; view: CostView }) => void;
}) {
  const months = useMemo(() => {
    const list = recentMonths(new Date());
    return list.includes(month) ? list : [month, ...list];
  }, [month]);
  const q = useQuery(costToServeQuery(api, month));
  return (
    <OpsFrame>
      <LocationHeader
        crumbs={[operatorCrumb, { label: "Cost to serve" }]}
        icon={ReceiptIcon}
        title="Cost to serve"
        count={q.data ? q.data.des.length : undefined}
      />
      <Toolbar bleed>
        <NativeSelect
          size="sm"
          aria-label="Month"
          value={month}
          onChange={(e) => onChange({ month: e.target.value, view })}
          className="w-auto shrink-0"
        >
          {months.map((m) => (
            <NativeSelectOption key={m} value={m}>
              {monthLabel(m)}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <span className="ml-auto min-w-0 truncate text-xs text-muted-foreground">
          Charged is what the client pays; cost is model, supervision and other agency costs.
        </span>
      </Toolbar>
      <OpsBody>
        {q.isPending ? (
          <Loading label="Loading cost to serve" rows={5} />
        ) : q.isError ? (
          <LoadError what="Cost to serve" error={q.error} />
        ) : (
          <>
            <CoverageNotice complete={q.data.complete} reasons={q.data.incomplete_reasons} />
            {q.data.des.length === 0 ? (
              <EmptyState
                variant="card"
                icon={ReceiptIcon}
                title={`No costs in ${monthLabel(month)}`}
                description="Charges and costs booked on your companies' digital employees show up here."
              />
            ) : (
              <>
                <CostTiles data={q.data} />
                <ViewTabs
                  aria-label="Cost to serve views"
                  value={view}
                  onValueChange={(v) => onChange({ month, view: v as CostView })}
                  tabs={COST_VIEWS.map((v) => ({
                    value: v,
                    label: VIEW_LABELS[v],
                    count: q.data[v].length,
                  }))}
                >
                  <TabsContent value="des" className="pt-3">
                    <DeTable data={q.data} />
                  </TabsContent>
                  <TabsContent value="clients" className="pt-3">
                    <MoneyTable
                      slot="cost-clients"
                      caption="Each client company in its reporting currency."
                      first="Client"
                      rows={q.data.clients.map((c) => ({
                        key: c.company.id,
                        name: c.company.name,
                        sub: `${c.de_count} ${c.de_count === 1 ? "DE" : "DEs"}`,
                        money: c,
                        actions: (
                          <RowLinkAction
                            icon={ArrowUpRightIcon}
                            label={`Open ${c.company.name}`}
                            render={<Link to="/c/$slug" params={{ slug: c.company.slug }} />}
                          />
                        ),
                      }))}
                    />
                  </TabsContent>
                  <TabsContent value="templates" className="pt-3">
                    <MoneyTable
                      slot="cost-templates"
                      caption="Each template per currency; companies in different currencies are never summed."
                      first="Template"
                      rows={q.data.templates.map((t) => ({
                        key: `${t.template_key}:${t.currency}`,
                        name: t.template_key,
                        sub: `${t.de_count} ${t.de_count === 1 ? "DE" : "DEs"} · ${t.companies} ${t.companies === 1 ? "company" : "companies"} · ${t.currency}`,
                        money: t,
                      }))}
                    />
                  </TabsContent>
                </ViewTabs>
              </>
            )}
          </>
        )}
      </OpsBody>
    </OpsFrame>
  );
}

/** The month in one currency (the one most clients report in); the others are named. */
function CostTiles({ data }: { data: CostToServe }) {
  const byCurrency = new Map<string, MoneyRow[]>();
  for (const c of data.clients)
    byCurrency.set(c.currency, [...(byCurrency.get(c.currency) ?? []), c]);
  const [currency, rows] = [...byCurrency.entries()].sort(
    (a, b) => b[1].length - a[1].length,
  )[0] ?? ["USD", []];
  const sum = (k: keyof MoneyRow) =>
    rows.reduce((a, r) => a + (typeof r[k] === "number" ? (r[k] as number) : 0), 0);
  const charged = sum("charged_minor");
  const cost = sum("cost_minor");
  const margin = sum("margin_minor");
  const minutes = data.des
    .filter((d) => d.currency === currency)
    .reduce((a, d) => a + d.supervision_minutes, 0);
  const others = [...byCurrency.keys()].filter((c) => c !== currency);
  const note = others.length ? `${currency}; ${others.join(", ")} apart` : currency;
  const pct = charged > 0 ? Math.round((margin / charged) * 100) : null;
  return (
    <Tiles data-slot="cost-tiles" aria-label={`Cost to serve totals in ${currency}`}>
      <StatTile
        size="sm"
        label="Charged"
        icon={ReceiptIcon}
        value={formatMoney(charged, currency)}
        detail={note}
      />
      <StatTile
        size="sm"
        label="Cost to serve"
        icon={Building2Icon}
        value={formatMoney(cost, currency)}
        detail={`model, supervision, other · ${note}`}
      />
      <StatTile
        size="sm"
        label="Margin"
        icon={TrendingUpIcon}
        value={<Money minor={margin} currency={currency} loss />}
        detail={pct === null ? "nothing charged" : `${pct}% of charged`}
      />
      <StatTile
        size="sm"
        label="Supervision time"
        icon={TimerIcon}
        value={hMm(minutes)}
        detail="h:mm on the month's DEs"
      />
    </Tiles>
  );
}

const MONEY_HEADS = ["Charged", "Model", "Supervision", "Other", "Cost", "Margin"] as const;

function MoneyHeads() {
  return (
    <>
      {MONEY_HEADS.map((h) => (
        <TableHead key={h} scope="col" className="text-right">
          {h}
        </TableHead>
      ))}
    </>
  );
}

/** Margin as text over a bar of its share of what was charged (red when a loss). */
function MarginCell({ m }: { m: MoneyRow }) {
  const share = m.charged_minor > 0 ? (m.margin_minor / m.charged_minor) * 100 : 0;
  return (
    <TableCell className="text-right tabular-nums" data-slot="margin">
      <span className="inline-flex min-w-28 flex-col items-end gap-1">
        <Money minor={m.margin_minor} currency={m.currency} loss />
        <InlineBar
          size="xs"
          value={Math.abs(share)}
          tone={m.margin_minor < 0 ? "danger" : share < 20 ? "warning" : "success"}
          className="w-20"
          aria-hidden="true"
        />
      </span>
    </TableCell>
  );
}

function MoneyCells({ m }: { m: MoneyRow }) {
  return (
    <>
      {(
        [m.charged_minor, m.model_minor, m.supervision_minor, m.other_minor, m.cost_minor] as const
      ).map((v, i) => (
        <TableCell key={MONEY_HEADS[i]} className="text-right tabular-nums">
          <Money minor={v} currency={m.currency} />
        </TableCell>
      ))}
      <MarginCell m={m} />
    </>
  );
}

function DeTable({ data }: { data: CostToServe }) {
  return (
    <Table data-slot="cost-des">
      <TableCaption>
        One row per digital employee; time is the supervision and founder time behind the
        supervision cost, in h:mm.
      </TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead scope="col">Digital employee</TableHead>
          <MoneyHeads />
          <TableHead scope="col" className="text-right">
            Time
          </TableHead>
          <TableHead scope="col">
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.des.map((d) => (
          <TableRow
            key={d.de.membership_id}
            data-de={d.de.membership_id}
            className={TABLE_ROW_HOVER}
          >
            <TableHead scope="row" className="max-w-56 font-normal whitespace-normal">
              <WorkerBadge kind="de" size="sm" className="mr-1 align-middle" />
              <Link
                to="/c/$slug/agents/de/$id"
                params={{ slug: d.company.slug, id: d.de.membership_id }}
                className="font-medium underline-offset-4 hover:underline"
              >
                {workerName(d.de.display_name)}
              </Link>
              <RowSub>
                {d.company.name} · {d.template_key}
                {d.seat ? ` · ${d.seat.title}` : ""}
              </RowSub>
            </TableHead>
            <MoneyCells m={d} />
            <TableCell className="text-right tabular-nums">{hMm(d.supervision_minutes)}</TableCell>
            <TableCell className="text-right">
              <RowActions>
                <RowLinkAction
                  icon={BotIcon}
                  label={`Open ${workerName(d.de.display_name)}`}
                  render={
                    <Link
                      to="/c/$slug/agents/de/$id"
                      params={{ slug: d.company.slug, id: d.de.membership_id }}
                    />
                  }
                />
                <RowLinkAction
                  icon={ArrowUpRightIcon}
                  label={`Open ${d.company.name}`}
                  render={<Link to="/c/$slug" params={{ slug: d.company.slug }} />}
                />
              </RowActions>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function MoneyTable({
  slot,
  caption,
  first,
  rows,
}: {
  slot: string;
  caption: string;
  first: string;
  rows: { key: string; name: string; sub: ReactNode; money: MoneyRow; actions?: ReactNode }[];
}) {
  return (
    <Table data-slot={slot}>
      <TableCaption>{caption}</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead scope="col">{first}</TableHead>
          <MoneyHeads />
          <TableHead scope="col">
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.key} className={TABLE_ROW_HOVER}>
            <TableHead scope="row" className="font-normal">
              <div className="font-medium">{r.name}</div>
              <RowSub>{r.sub}</RowSub>
            </TableHead>
            <MoneyCells m={r.money} />
            <TableCell className="text-right">
              {r.actions ? <RowActions>{r.actions}</RowActions> : null}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
